import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import { ORDER_STATUS, statusColor, accentColorForType } from 'common/src/other/PartnerUtils';
import {
  Box, Typography, Stack, Paper, Tabs, Tab, Chip, Avatar
} from '@mui/material';
import moment from 'moment/min/moment-with-locales';
import { FONT_FAMILY } from '../common/sharedFunctions';

const ACTIVE_STATUSES = [
  ORDER_STATUS.PENDING,
  ORDER_STATUS.CONFIRMED,
  ORDER_STATUS.IN_PREPARATION,
  ORDER_STATUS.READY,
  ORDER_STATUS.OUT_FOR_DELIVERY,
];

export default function MyPartnerOrders() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((s) => s.auth);
  const partnerorderdata = useSelector((s) => s.partnerorderdata);
  const [tab, setTab] = useState(0);
  const { fetchPartnerOrdersByCustomer } = api;

  useEffect(() => {
    if (auth?.profile?.uid) {
      dispatch(fetchPartnerOrdersByCustomer(auth.profile.uid));
    }
  }, [dispatch, fetchPartnerOrdersByCustomer, auth?.profile?.uid]);

  const orders = partnerorderdata.orders || [];
  const filtered = useMemo(() => {
    return orders
      .filter((o) =>
        tab === 0
          ? ACTIVE_STATUSES.includes(o.status)
          : !ACTIVE_STATUSES.includes(o.status)
      )
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [orders, tab]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT_FAMILY }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        {t('my_orders') || 'Mis reservas'}
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={t('active') || 'Activas'} />
        <Tab label={t('history') || 'Historial'} />
      </Tabs>
      <Stack spacing={1.25}>
        {filtered.map((o) => (
          <Paper
            key={o.id} variant="outlined"
            sx={{ p: 1.5, borderRadius: 3, cursor: 'pointer' }}
            onClick={() => navigate(`/myorders/${o.id}`)}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar variant="rounded" src={o.partnerCover || undefined} sx={{ width: 64, height: 64 }}>
                {(o.partnerName || '?').slice(0, 1)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontWeight: 600 }} noWrap>
                    {o.partnerName}
                  </Typography>
                  <Chip
                    size="small"
                    label={o.partnerType}
                    sx={{
                      backgroundColor: `${accentColorForType(o.partnerType)}1a`,
                      color: accentColorForType(o.partnerType),
                      fontWeight: 600,
                    }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  #{o.reference} · {moment(o.createdAt).format('lll')}
                </Typography>
              </Box>
              <Stack alignItems="flex-end" spacing={0.5}>
                <Chip
                  size="small"
                  label={o.status}
                  sx={{ backgroundColor: `${statusColor(o.status)}1a`, color: statusColor(o.status), fontWeight: 600 }}
                />
                <Typography sx={{ fontWeight: 700 }}>
                  {o.currency} {Number(o.total || 0).toFixed(2)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        ))}
        {filtered.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {t('no_orders') || 'Sin reservas'}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
